import _ from "lodash";
import dayjs from "dayjs";

// Umbrales de la cinta de huecos entre unidades (minutos de recorrido)
export const GAP_PEGADAS = 7;
export const GAP_HUECO = 17;
export const GAP_OBJETIVO = 12;

// Minutos a partir de los cuales una unidad se considera en riesgo / adelantada.
// "Quemada" siempre es diferencia > tolerancia del checkpoint, igual que el motor.
export const MIN_RIESGO = 3;
export const MIN_ADELANTO = -3;

export const ESTADOS = {
  ade: { label: "Adelantada", color: "#0ac7fb", ink: "#0a7f97", bg: "#daf7fe", bd: "#9de9fd" },
  tie: { label: "En tiempo", color: "#3cd188", ink: "#1d6b47", bg: "#e2f8ed", bd: "#b1edcf" },
  rie: { label: "En riesgo de quemarse", color: "#ffc701", ink: "#9a7500", bg: "#fff7d9", bd: "#ffe999" },
  que: { label: "Salida quemada", color: "#f7666e", ink: "#c0464e", bg: "#fee8e9", bd: "#fcc2c5" },
  sin: { label: "Sin lectura de checkpoint", color: "#ced4da", ink: "#6b7280", bg: "#f3f6f9", bd: "#e9ebec" },
};

const LINE_TOP = 64;
const LANE_H = 36;
const LANE_MIN_PCT = 3.6;

export const hhmm = (date) => (date ? dayjs(date).format("HH:mm") : "—");

export const devTxt = (d) => {
  if (d === null || d === undefined) return "—";
  if (d > 0) return `+${d}′`;
  if (d < 0) return `${d}′`;
  return "a tiempo";
};

export const getEstado = (desv, tolerancia) => {
  if (desv === null || desv === undefined) return "sin";
  if (desv > tolerancia) return "que";
  if (desv >= MIN_RIESGO) return "rie";
  if (desv <= MIN_ADELANTO) return "ade";
  return "tie";
};

const scoreKind = (score) => (score > 8 ? "que" : score > 3 ? "rie" : "tie");

// Desviacion actual de una salida: diferencia (con signo) del ultimo checkpoint con tiempo_real
export const getDesviacion = (salida) => {
  const geos = _.sortBy(salida?.time_geofences ?? [], "position");
  const ultimo = _.findLast(geos, (g) => g.tiempo_real != null) ?? null;
  const siguiente = ultimo
    ? geos.find((g) => g.position > ultimo.position) ?? null
    : geos.find((g) => g.tiempo_real == null) ?? null;
  const desv = ultimo?.diferencia != null ? Number(ultimo.diferencia) : null;

  return {
    desv,
    tolerancia: Number(ultimo?.tolerancy ?? 0),
    ultimo,
    siguiente,
    real: geos[0]?.tiempo_real ?? null,
    quemadas: geos.filter((g) => Number(g.quemadas) === 1).length,
  };
};

// Punto de retorno: la parada tras la cual los alias empiezan a repetirse (ida y vuelta).
// Si la unica repeticion es la base final (circuito), no hay retorno.
export const detectTurn = (aliases) => {
  const seen = new Set();
  for (let i = 0; i < aliases.length - 2; i++) {
    seen.add(aliases[i]);
    if (seen.has(aliases[i + 1])) return i;
  }
  return null;
};

export const buildStations = (branch) => {
  const geos = (branch?.geofences ?? []).filter((g) => g.percent != null);
  const aliases = geos.map((g) => g.geo?.alias ?? "-");
  const turn = detectTurn(aliases);

  const stations = geos.map((g, i) => {
    const isTurn = i === turn;
    const major = i === 0 || i === geos.length - 1 || !!g.is_base || isTurn;
    const size = major ? 11 : 8;
    return {
      key: `${g.id}-${i}`,
      name: aliases[i],
      left: Math.min(100, Math.max(0, Number(g.percent))),
      size,
      major,
      isTurn,
      labelTop: i % 2 === 0 ? 86 : 100,
    };
  });

  return { stations, turn: turn != null ? stations[turn] : null };
};

const buildUnit = (salida, ramalNombre) => {
  const info = getDesviacion(salida);
  const estado = getEstado(info.desv, info.tolerancia);
  const percent = salida?.unit?.percent;
  return {
    id: salida.id,
    numero: String(salida?.unit?.number ?? salida?.unit_name ?? "-"),
    ramal: ramalNombre,
    percent: percent != null ? Math.min(100, Math.max(0, Number(percent))) : null,
    // Minutos de recorrido segun la posicion (time_dst); si el backend aun no lo envia se usan
    // los minutos transcurridos desde la salida (unit.time), que dan un intervalo equivalente
    timeDst: salida?.unit?.time_dst != null ? Number(salida.unit.time_dst) : null,
    timeElapsed: salida?.unit?.time != null ? Number(salida.unit.time) : null,
    estado,
    ...info,
    salida,
  };
};

const placeUnits = (units) => {
  let prev = -99;
  let lane = 0;
  return _.sortBy(units, "percent").map((u, i) => {
    lane = u.percent - prev < LANE_MIN_PCT ? lane + 1 : 0;
    prev = u.percent;
    return { ...u, lane, z: 10 + i };
  });
};

const buildGaps = (placed) =>
  placed.slice(1).reduce((gaps, u, i) => {
    const a = placed[i];
    const field = a.timeDst != null && u.timeDst != null ? "timeDst" : "timeElapsed";
    if (a[field] == null || u[field] == null) return gaps;
    const min = Math.round(Math.abs(u[field] - a[field]));
    const kind = min < GAP_PEGADAS ? "que" : min > GAP_HUECO ? "rie" : "tie";
    gaps.push({
      key: `${a.id}-${u.id}`,
      min,
      left: a.percent,
      width: u.percent - a.percent,
      bg: ESTADOS[kind].bg,
      bd: ESTADOS[kind].bd,
      hueco: min > GAP_HUECO,
      entre: [a.numero, u.numero],
    });
    return gaps;
  }, []);

export const countEstados = (units) => {
  const c = { ade: 0, tie: 0, rie: 0, que: 0, sin: 0 };
  units.forEach((u) => c[u.estado]++);
  return c;
};

export const buildRamal = (branch, salidas, index) => {
  const nombre = branch?.name ?? "-";
  const numero = String(nombre).match(/^\s*(\d+)/)?.[1] ?? String(index + 1);
  const { stations, turn } = buildStations(branch);
  const units = (salidas ?? []).map((s) => buildUnit(s, nombre));
  const located = placeUnits(units.filter((u) => u.percent != null));
  const sinUbicacion = units.filter((u) => u.percent == null).map((u) => u.numero);
  const maxLane = located.reduce((m, u) => Math.max(m, u.lane), 0);
  const gaps = buildGaps(located);
  const c = countEstados(units);

  const late = units.filter((u) => u.desv > 0);
  const score = late.length ? _.sumBy(late, "desv") / late.length : 0;

  const meta = [
    `${stations.length} puntos`,
    turn ? `retorno en ${turn.name}` : null,
    sinUbicacion.length ? `sin ubicación: ${sinUbicacion.join(", ")}` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return {
    id: branch.id,
    numero,
    nombre,
    meta,
    stations,
    turnLeft: turn ? turn.left : null,
    units,
    located,
    gaps,
    counts: c,
    sev: c.que * 10 + c.rie,
    score: score.toFixed(1),
    scoreEstado: ESTADOS[scoreKind(score)],
    offset: maxLane * LANE_H,
    lineTop: LINE_TOP + maxLane * LANE_H,
    unitTop: (lane) => LINE_TOP - 28 + (maxLane - lane) * LANE_H,
  };
};

export const buildTotals = (rows) => {
  const all = rows.flatMap((r) => r.units);
  const c = countEstados(all);
  const conLectura = all.length - c.sin || 1;
  const w = (k) => `${(c[k] / conLectura) * 100}%`;

  const peorRamal = _.maxBy(rows, (r) => r.counts.que);
  const peorHueco = _.maxBy(
    rows.flatMap((r) => r.gaps.filter((g) => g.hueco).map((g) => ({ ...g, ramal: r.nombre }))),
    "min"
  );

  const partes = [];
  if (peorRamal && peorRamal.counts.que > 0) {
    partes.push(
      `${peorRamal.counts.que} ${peorRamal.counts.que === 1 ? "salida quemada" : "salidas quemadas"} en ramal ${peorRamal.nombre}`
    );
  } else if (c.rie > 0) {
    partes.push(`Sin quemadas · ${c.rie} ${c.rie === 1 ? "unidad" : "unidades"} en riesgo`);
  }
  if (peorHueco) {
    partes.push(
      `hueco de ${peorHueco.min}′ entre unidades ${peorHueco.entre[0]} y ${peorHueco.entre[1]} (${peorHueco.ramal})`
    );
  }

  const atencion = partes.join(" — ");

  return {
    act: all.length,
    counts: c,
    pct: all.length - c.sin > 0 ? `${Math.round(((c.ade + c.tie) / conLectura) * 100)}%` : "—",
    widths: { ade: w("ade"), tie: w("tie"), rie: w("rie"), que: w("que") },
    atencion: atencion ? atencion.charAt(0).toUpperCase() + atencion.slice(1) : "Sin incidencias en la ruta.",
    all,
  };
};
